/**
 * FinancialDataset — strukturierte BWA-/EKS-Daten für eine Fallakte.
 *
 * Geld wird grundsätzlich als `MoneyCents` (Integer-Cents) geführt — siehe
 * `brands.ts`. Damit ist Float-Drift in Aggregationen ausgeschlossen.
 *
 * `monthlyAggregates` ist eine Liste von Monatsdaten (BWA-zeilenähnlich).
 * Die Score-Engine arbeitet auf Aggregaten, nicht auf Einzelbuchungen.
 */
import { z } from 'zod';
import { HoursSchema, MoneyCentsSchema } from './brands.js';
import { BgCompositionSchema } from './bgComposition.js';
import { CaseIdSchema, FinancialDatasetIdSchema } from './ids.js';

/** ISO-Monatsangabe `YYYY-MM`. */
export const YearMonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YearMonth muss YYYY-MM sein');
export type YearMonth = z.infer<typeof YearMonthSchema>;

export const MonthlyAggregateSchema = z.object({
  yearMonth: YearMonthSchema,
  /** Brutto-Umsatz in Cent */
  revenueCents: MoneyCentsSchema,
  /** Summe Betriebsausgaben in Cent (positiv ausgewiesen) */
  costsCents: MoneyCentsSchema,
  /** Privatentnahmen in Cent (positiv ausgewiesen) */
  privateWithdrawalsCents: MoneyCentsSchema.optional(),
});
export type MonthlyAggregate = z.infer<typeof MonthlyAggregateSchema>;

export const HourlyRateInputSchema = z.object({
  /** Selbst angegebener oder dokumentierter Stundensatz pro Stunde, NETTO, in Cent */
  hourlyRateNetCents: MoneyCentsSchema.optional(),
  /** Tatsächlich abrechenbare Stunden pro Monat */
  billableHoursPerMonth: HoursSchema.optional(),
  /** Branchenüblicher Untergrenz-Stundensatz, brutto, in Cent */
  industryFloorHourlyRateCents: MoneyCentsSchema.optional(),
});
export type HourlyRateInput = z.infer<typeof HourlyRateInputSchema>;

export const LiquiditySnapshotSchema = z.object({
  /** Liquide Mittel auf Konten zum Stichtag */
  liquidAssetsCents: MoneyCentsSchema,
  /** Kurzfristige Verbindlichkeiten zum Stichtag */
  shortTermLiabilitiesCents: MoneyCentsSchema.default(0),
  /** Stichtag */
  asOf: z.coerce.date(),
});
export type LiquiditySnapshot = z.infer<typeof LiquiditySnapshotSchema>;

export const FinancialDatasetSchema = z.object({
  id: FinancialDatasetIdSchema,
  caseId: CaseIdSchema,
  /** Monatsaggregate (mind. ein Monat, bevorzugt 6-12 Monate) */
  monthlyAggregates: z
    .array(MonthlyAggregateSchema)
    .min(1, 'Mindestens ein Monat Datengrundlage erforderlich'),
  hourlyRate: HourlyRateInputSchema.default({}),
  liquidity: LiquiditySnapshotSchema.optional(),
  bgComposition: BgCompositionSchema,
  /**
   * Brutto-Erwerbseinkommen für die SGB-II-Anrechnung (§ 11 SGB II).
   *
   * Anrechnung erfolgt auf Brutto, nicht auf Gewinn — daher first-class
   * Feld. Wenn nicht gesetzt, fällt der Adapter in der UI auf
   * `max(0, revenue - costs)` zurück und markiert den Score als geschätzt.
   * Die formelle Stellungnahme darf NIE auf einem geschätzten Wert basieren.
   */
  bruttoErwerbseinkommenCents: MoneyCentsSchema.optional(),
  /**
   * Sonstiges anrechenbares Einkommen der Bedarfsgemeinschaft pro Monat
   * (§ 11 Abs. 1 SGB II), z.B. Kindergeld, Elterngeld, Unterhalt, Renten.
   * Wird ohne Erwerbstätigen-Freibetrag voll auf den Bedarf angerechnet und
   * senkt damit die Tragfähigkeitslücke. Getrennt vom Erwerbseinkommen erfasst,
   * weil die § 11b-Freibeträge nur auf Erwerbseinkommen wirken.
   */
  sonstigesEinkommenCents: MoneyCentsSchema.optional(),
  /**
   * Tatsächliche monatliche Burn-Rate (Fixkosten-Abfluss) für die
   * Liquiditäts-Reichweite. Wird im Wizard erfasst; fehlt sie, fällt die
   * Runway-Rechnung auf die durchschnittlichen Betriebsausgaben zurück.
   */
  monthlyBurnRateCents: MoneyCentsSchema.optional(),
  /**
   * Privatentnahmen pro Monat (optional — getrennt vom Profit erfasst,
   * weil sie für die SGB-II-Lebenshaltung relevant sind, aber nicht für
   * den unternehmerischen Gewinn).
   */
  privatentnahmenCents: MoneyCentsSchema.optional(),
  /** Quellen-Hinweis (BWA, EKS, manuell) */
  sourceType: z.enum(['bwa_pdf', 'bwa_xlsx', 'eks_pdf', 'eks_xlsx', 'manual']),
  uploadedFileNames: z.array(z.string().min(1).max(256)).default([]),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type FinancialDataset = z.infer<typeof FinancialDatasetSchema>;
